
import {
  Component,
  Provide,
  Prop,
  Watch,
  Mixins
} from "vue-property-decorator";
import { mapState } from "vuex";
import _ from "@/boot/lodash";
import { CreateElement } from "vue";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import BillingCyclesStore from "@/mixins/constants/billingCyclesStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import { IProvider } from "@/models/providers";
import {
  IAffiliateTierConditionsProviderData,
  IAffiliateTierConditionsProviderMethods
} from "@/models/providers/affiliateTierConditions";
import { IAffiliateTierConditionForm } from "@/models/affiliate";
import { IAffiliateTier, IAffiliateTierCondition } from "@/models/affiliate";
import { IProduct } from "@/models/products";
import { IState } from "@/store";

@Component({
  computed: {
    ...mapState({
      affiliateTierConditionsScope(state: IState, getters) {
        return getters[
          `data/${DataModules.AFFILIATES_TIERS_CONDITIONS}/listScope`
        ](this.affiliateTierId);
      },
      affiliateTierConditions(state: IState, getters) {
        return _.isNil(this.affiliateTierId)
          ? this.formConditions
          : getters[`data/listArray`]({
              storeModule: DataModules.AFFILIATES_TIERS_CONDITIONS,
              scope: this.affiliateTierConditionsScope
            });
      }
    })
  }
})
export default class AffiliateTierConditionsProvider
  extends Mixins(RequestPromises, BillingCyclesStore)
  implements IProvider {
  // State

  public affiliateTierConditionsScope!: string;
  public affiliateTierConditions!: IAffiliateTierCondition | any;

  // Props

  @Prop({ required: true })
  public affiliateTierId!: IAffiliateTier["id"];

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Data

  public isLoading = false;
  public isReloading = false;
  public hasRequestError = false;
  public isDeleting = false;

  public formConditions: IAffiliateTierCondition[] = [];

  // Lifecycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.isLoading = true;
    await this.loadData(false);

    this.$bus.$on("refreshAffiliateTierConditions", () => {
      this.loadData(true);
    });
  }

  public async loadData(ignoreStored) {
    try {
      await Promise.all([
        this.getBillingCycles(),
        this.getAffiliateTierConditions(ignoreStored)
      ]);

      this.hasRequestError = false;
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData(true);
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.AFFILIATES_TIERS_CONDITIONS,
      scope: this.affiliateTierConditionsScope,
      vm: this
    });

    this.$bus.$off("refreshAffiliateTierConditions");
  }

  // Methods

  public getAffiliateTierConditions(ignoreStored) {
    if (!this.affiliateTierId) return Promise.resolve();
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_TIERS_CONDITIONS}/list`,
      {
        id: this.affiliateTierId,
        scope: this.affiliateTierConditionsScope,
        params: {
          order: "order",
          with: "products",
          limit: 0
        },
        vm: this,
        ignoreStored
      }
    );
  }

  public openManageConditionModal(
    affiliateTierConditionId: IAffiliateTierCondition["id"] | null = null,
    affiliateTierCondition: IAffiliateTierCondition | null = null,
    events
  ) {
    this.$store.dispatch(
      `data/${DataModules.AFFILIATES_TIERS_CONDITIONS}/openAddEditModal`,
      {
        props: {
          affiliateTierId: this.affiliateTierId,
          affiliateTierConditionId,
          affiliateTierCondition
        },
        events
      }
    );
  }

  public createTierCondition(
    data: IAffiliateTierConditionForm,
    affiliateTierId = null
  ) {
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_TIERS_CONDITIONS}/create`,
      {
        id: _.isNil(affiliateTierId) ? this.affiliateTierId : affiliateTierId,
        data
      }
    );
  }

  public attachSelectedProducts(
    productIds: IProduct["id"][],
    affiliateTierId: IAffiliateTier["id"],
    affiliateTierConditionId: IAffiliateTierCondition["id"]
  ) {
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_TIERS_CONDITIONS}/attachSelectedProducts`,
      {
        tierId: affiliateTierId,
        conditionId: affiliateTierConditionId,
        data: {
          product_ids: productIds,
          affiliate_tier_condition_id: affiliateTierConditionId
        }
      }
    );
  }

  public async deleteTierCondition(
    affiliateTierConditionId: IAffiliateTierCondition["id"]
  ) {
    if (!this.canDelete) return;
    const modal = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        parent: this,
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_confirmation", {
            object: this.$t("_.affiliate_tier_condition")
              .toString()
              .toLowerCase()
          })
        },
        canCancel: [],
        events: {
          confirmed: async () => {
            try {
              this.isDeleting = true;
              await this.deleteCondition(affiliateTierConditionId);
              await this.getAffiliateTierConditions(true);

              this.$store.dispatch("toast/show", {
                message: this.$t(
                  "_.affiliate_tier_condition_successfully_removed"
                )
              });
              modal.close();
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            } finally {
              this.isDeleting = false;
            }
          }
        }
      }
    });
  }

  public deleteCondition(
    affiliateTierConditionId: IAffiliateTierCondition["id"]
  ) {
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_TIERS_CONDITIONS}/remove`,
      {
        tierId: this.affiliateTierId,
        id: affiliateTierConditionId
      }
    );
  }

  public reorderConditions(conditionIds: IAffiliateTierCondition["id"][]) {
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_TIERS_CONDITIONS}/reorderTierConditions`,
      {
        tierId: this.affiliateTierId,
        data: {
          affiliate_tier_condition_ids: conditionIds
        }
      }
    );
  }

  // Computed

  public get canCreate() {
    return this.$userCan("create_affiliate_tier_condition");
  }

  public get canUpdate() {
    return this.$userCan("update_affiliate_tier_condition");
  }

  public get canDelete() {
    return this.$userCan("delete_affiliate_tier_condition");
  }

  // Watchers

  @Watch("formConditions")
  public onFormConditionsChange(value) {
    this.$emit("updateFormConditions", value);
  }

  // Guarding

  public guardFunctionalities = ["list_affiliate_tier_conditions"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("tierConditionsProviderData")
  public providerData(): IAffiliateTierConditionsProviderData {
    return {
      canCreate: this.canCreate,
      canUpdate: this.canUpdate,
      canDelete: this.canDelete,
      affiliateTierConditions: this.affiliateTierConditions,
      billingCycles: this.billingCycles,
      hasRequestError: this.hasRequestError,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      isDeleting: this.isDeleting
    };
  }

  @Provide("tierConditionsProviderMethods")
  public get providerMethods(): IAffiliateTierConditionsProviderMethods {
    return {
      openManageConditionModal: this.openManageConditionModal,
      createTierCondition: this.createTierCondition,
      attachSelectedProducts: this.attachSelectedProducts,
      deleteTierCondition: this.deleteTierCondition,
      reorderConditions: this.reorderConditions,
      getAffiliateTierConditions: this.getAffiliateTierConditions,
      reloadData: this.reloadData
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (!this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $affTierConditionsData: this.providerData(),
        $affTierConditionsMethods: this.providerMethods
      })
    );
  }
}
